import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../../services/auth/authService';
import startActivityTimestampLogger, { startIdleMonitor } from '../../../services/auth/idleTrackerService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // stopAllRef holds cleanup for logger + idle monitor
  const stopAllRef = useRef<() => void>(() => {});

  // When true we have logged the user out due to idle and should NOT redirect to /login
  const suppressRedirectOnIdleRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    // Move the activity logger + idle monitor startup into a helper.
    // Returns a stop() function which stops both logger and idle monitor.
    const initIdleLogout = (timeoutMs: number) => {
      // start activity timestamp logger (updates lastActivity in sessionStorage)
      const stopLogger = startActivityTimestampLogger();

      // start idle monitor which triggers a forced logout (but no navigation)
      const stopIdle = startIdleMonitor(timeoutMs, async () => {
        console.log('Idle timeout reached - forcing logout (no redirect)');
        // mark that idle logout occurred so we don't auto-redirect elsewhere
        suppressRedirectOnIdleRef.current = true;

        try {
          await authService.logout();
        } catch (e) {
          console.error('Error during logout on idle', e);
        } finally {
          // stop monitors but DO NOT navigate to /login here
          try { stopLogger(); } catch {
            console.log('Error stopping activity logger on idle logout — ignoring.');
          }
          try { stopIdle(); } catch {
            console.log('Error stopping idle monitor on idle logout — ignoring.');
          }

          if (!mounted) return;
          // reflect logged-out state so UI can respond; do not auto-redirect
          setIsAuthenticated(false);
        }
      });

      // return composite stop function
      return () => {
        try { stopIdle(); } catch {}
        try { stopLogger(); } catch {}
      };
    };

    const checkAuthentication = async () => {
      try {
        console.log('ProtectedRoute: Checking authentication...', { pathname: location.pathname });

        const accessToken = sessionStorage.getItem('accessToken');
        console.log('Access token exists:', !!accessToken);

        if (!accessToken) {
          console.log('No access token - redirecting to login');
          if (!mounted) return;
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        console.log('Token found - validating...');
        const isAuth = await authService.checkAndRefreshAuth();

        if (isAuth) {
          console.log('User authenticated');

          // previously inline: startLogger + startIdleMonitor...
          // now delegated to helper which returns a stop() function
          const stopAll = initIdleLogout(60 * 60 * 1000); // 60 minutes (1 hour)
          stopAllRef.current = stopAll;

          // Also log that activity tracking has been started now that user is authenticated
          console.log('Activity tracking started');

          if (!mounted) return;
          setIsAuthenticated(true);
        } else {
          console.log('Authentication validation failed');
          if (!mounted) return;
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Auth check error:', error);
        if (!mounted) return;
        setIsAuthenticated(false);
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    checkAuthentication();

    return () => {
      mounted = false;
      try { stopAllRef.current && stopAllRef.current(); } catch {}
    };
  }, [location.pathname, navigate]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // If the user was logged out due to idle, do NOT redirect to /login per request.
    if (suppressRedirectOnIdleRef.current) {
      console.log('🔕 User logged out due to inactivity — suppressing automatic redirect to /login.');
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <p>Your session expired due to inactivity.</p>
        </div>
      );
    }

    // Normal unauthenticated flow: save return path and redirect to login
    try {
      const returnTo = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
      if (returnTo?.startsWith('/')) {
        sessionStorage.setItem('postLoginRedirect', returnTo);
        console.log('Saved post-login redirect:', returnTo);
      }
    } catch (e) {
      console.warn('Could not persist post-login redirect', e);
    }

    console.log('🔴 Redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  console.log('🟢 Rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;